import React, { createContext, PropsWithChildren, useContext, useMemo, useRef, useState } from 'react';
import { CreatedUpdatedModal } from 'vl-common/src/components/CreatedUpdatedModal';
import { ResentInvite } from 'vl-common/src/components/Referrers/ResentInvite';
import { EditEmailModal } from 'vl-common/src/components/Referrers/EditEmailModal';
import AddPatient from 'vl-common/src/components/AddPatient/AddPatient';
import { ViewNotesModal } from 'vl-common/src/components/Appointments/ViewNotesModal';
import AuditTrailModal from 'vl-common/src/components/Audit/AuditTrailModal';
import { User } from '../../schemas/shared';
import { successModal } from '../../components/Modals';
import CreateCaseModal from '../../components/Cases/CreateCaseModal';

export type PatientType = User & {
  [key: string]: unknown;
};

export type RefetchAfterActionComplete = (params?: { createdCaseId?: number }) => void;

type PatientManagementContextType = {
  onSetActionedPatient: (params: { patient: PatientType; refetchFn?: RefetchAfterActionComplete }) => void;
  openEditPatientModal: () => void;
  openResentInviteModal: () => void;
  openEditEmailModal: () => void;
  openPatientAuditModal: () => void;
  openViewNotesModal: () => void;
  openCreateCaseModal: () => void;
};

const PatientManagementContext = createContext<PatientManagementContextType | null>(null);

const ManagePatientContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [actionedPatient, setActionedPatient] = useState<PatientType | null>(null);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);
  const [showResentInviteModal, setShowResentInviteModal] = useState(false);
  const [showEditEmailModal, setShowEditEmailModal] = useState(false);
  const [showPatientAuditModal, setShowPatientAuditModal] = useState(false);
  const [showViewNotesModal, setShowViewNotesModal] = useState(false);
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);

  const [showInviteSentModal, setShowInviteSentModal] = useState(false);
  const [showEditedEmailModal, setShowEditedEmailModal] = useState(false);

  const onPatientUpdateHandler = useRef<RefetchAfterActionComplete | null>(null);
  const withResetActionedPatient = (fn: () => void) => () => {
    fn();
    setActionedPatient(null);
  };

  const openEditPatientModal = () => setShowEditPatientModal(true);
  const openResentInviteModal = () => setShowResentInviteModal(true);
  const openEditEmailModal = () => setShowEditEmailModal(true);
  const openPatientAuditModal = () => setShowPatientAuditModal(true);
  const openViewNotesModal = () => setShowViewNotesModal(true);
  const openInviteSentModal = () => setShowInviteSentModal(true);
  const openEditedEmailModal = () => setShowEditedEmailModal(true);
  const openCreateCaseModal = () => setShowCreateCaseModal(true);

  const closeEditPatientModal = withResetActionedPatient(() => setShowEditPatientModal(false));
  const closeResentInviteModal = withResetActionedPatient(() => setShowResentInviteModal(false));
  const closeEditEmailModal = withResetActionedPatient(() => setShowEditEmailModal(false));
  const closePatientAuditModal = withResetActionedPatient(() => setShowPatientAuditModal(false));
  const closeViewNotesModal = withResetActionedPatient(() => setShowViewNotesModal(false));
  const closeInviteSentModal = withResetActionedPatient(() => setShowInviteSentModal(false));
  const closEditedEmailModal = withResetActionedPatient(() => setShowEditedEmailModal(false));
  const closeCreateCaseModal = withResetActionedPatient(() => setShowCreateCaseModal(false));

  const onPatientEditComplete = () => {
    closeEditPatientModal();
    onPatientUpdateHandler?.current?.();
  };

  const onResendInviteComplete = () => {
    closeResentInviteModal();
    openInviteSentModal();
  };

  const onEditEmailComplete = () => {
    closeEditEmailModal();
    openEditedEmailModal();
    onPatientUpdateHandler?.current?.();
  };

  const onCloseInviteSentModal = () => {
    closeInviteSentModal();
    onPatientUpdateHandler?.current?.();
  };

  const onCreateCaseSuccess = (caseId: number) => {
    closeCreateCaseModal();
    onPatientUpdateHandler?.current?.({ createdCaseId: caseId });
    successModal({
      title: 'Success',
      content: 'The case has been created successfully',
      okButtonProps: { 'data-testid': 'case-created-success-button' }
    });
  };

  const onSetActionedPatient = ({
    patient,
    refetchFn
  }: {
    patient: PatientType;
    refetchFn?: RefetchAfterActionComplete;
  }) => {
    setActionedPatient(patient);

    if (refetchFn) {
      onPatientUpdateHandler.current = refetchFn;
    }
  };

  const contextValue = useMemo(() => {
    return {
      onSetActionedPatient,
      openEditPatientModal,
      openResentInviteModal,
      openEditEmailModal,
      openPatientAuditModal,
      openViewNotesModal,
      openCreateCaseModal
    };
  }, []);

  return (
    <PatientManagementContext.Provider value={contextValue}>
      {children}

      {actionedPatient && (
        <>
          <AddPatient
            close={closeEditPatientModal}
            show={showEditPatientModal}
            // @ts-expect-error
            savedPatientDetails={{
              ...actionedPatient,
              pref_tel: actionedPatient.tel_pref_no === 'mobile' ? 'M' : 'O',
              sex: actionedPatient.sex_code,
              tel_mobile_no: `${actionedPatient.tel_mobile_no}`.replace(/([+]\d{2})/g, ''), // remove +44 from start
              tel_other_no: `${actionedPatient.tel_other_no}`.replace(/([+]\d{2})/g, '') // remove +44 from start ,
            }}
            onCreated={onPatientEditComplete}
          />
          <ResentInvite
            isOpen={showResentInviteModal}
            toggle={closeResentInviteModal}
            cancelBtnHandler={closeResentInviteModal}
            onComplete={onResendInviteComplete}
            data={actionedPatient}
          />

          <EditEmailModal
            isOpen={showEditEmailModal}
            toggle={closeEditEmailModal}
            cancelBtnHandler={closeEditEmailModal}
            onComplete={onEditEmailComplete}
            data={actionedPatient}
            subText="Edit email address."
          />

          <ViewNotesModal
            show={showViewNotesModal}
            close={closeViewNotesModal}
            data={actionedPatient}
            getNotesParams={{
              patient_guid: actionedPatient?.user_guid
            }}
          />

          {showPatientAuditModal && (
            <AuditTrailModal
              key={actionedPatient?.user_guid}
              isOpen={showPatientAuditModal}
              toggle={closePatientAuditModal}
              user_guid={actionedPatient?.user_guid}
            />
          )}

          <CreateCaseModal
            isOpen={showCreateCaseModal}
            patient={actionedPatient}
            onCaseCreated={onCreateCaseSuccess}
            onCancel={closeCreateCaseModal}
          />
        </>
      )}

      <CreatedUpdatedModal
        isOpen={showInviteSentModal}
        toggle={onCloseInviteSentModal}
        okBtnHandler={onCloseInviteSentModal}
        title={'Invitation'}
        textDarkSide={'Email Sent'}
        subText={'The invitation email has been sent successfully.'}
      />

      <CreatedUpdatedModal
        isOpen={showEditedEmailModal}
        toggle={closEditedEmailModal}
        okBtnHandler={closEditedEmailModal}
        title={'Email'}
        textDarkSide={'Edited'}
        subText={'The Email address edited successfully.'}
      />
    </PatientManagementContext.Provider>
  );
};

export const useManagePatientContext = () => {
  const ctx = useContext(PatientManagementContext);

  if (!ctx) {
    throw new Error('useManagePatientContext must be used inside <ManagePatientContextProvider>');
  }

  return ctx;
};

export default ManagePatientContextProvider;
